import {
  GoogleAuthProvider,
  type IdTokenResult,
  type User,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
  signOut as firebaseSignOut,
} from 'firebase/auth'

import { getFirebaseAuth } from '@/lib/firebase/app'
import { callable } from '@/lib/firebase/callable'
import { parseCallableError } from '@/lib/firebase/errors'
import type { AuthClaims, AuthUser, MembershipPlan, WorkspaceRole } from '@/types/auth'
import type { SupportedLocale } from '@/i18n'

export const MAGIC_LINK_EMAIL_STORAGE_KEY = 'raldo:magic-link-email'

function toAuthUser(user: User): AuthUser {
  return {
    uid: user.uid,
    email: user.email,
    displayName: user.displayName,
  }
}

const WORKSPACE_ROLES: readonly WorkspaceRole[] = ['owner', 'admin', 'member']
const MEMBERSHIP_PLANS: readonly MembershipPlan[] = ['member', 'none']

function asWorkspaceRole(value: unknown): WorkspaceRole | undefined {
  return typeof value === 'string' && (WORKSPACE_ROLES as readonly string[]).includes(value)
    ? (value as WorkspaceRole)
    : undefined
}

function asMembershipPlan(value: unknown): MembershipPlan | undefined {
  return typeof value === 'string' && (MEMBERSHIP_PLANS as readonly string[]).includes(value)
    ? (value as MembershipPlan)
    : undefined
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function asBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined
}

function toAuthClaims(result: IdTokenResult): AuthClaims {
  const claims = result.claims
  return {
    workspaceId: asString(claims.workspaceId),
    brandId: asString(claims.brandId),
    role: asWorkspaceRole(claims.role),
    entitled: asBoolean(claims.entitled),
    plan: asMembershipPlan(claims.plan),
  }
}

export function observeAuthState(cb: (user: AuthUser | null) => void): () => void {
  return onAuthStateChanged(getFirebaseAuth(), (user) => {
    cb(user ? toAuthUser(user) : null)
  })
}

export async function readClaims(forceRefresh?: boolean): Promise<AuthClaims | null> {
  const user = getFirebaseAuth().currentUser
  if (!user) {
    return null
  }
  try {
    const result = await user.getIdTokenResult(forceRefresh)
    return toAuthClaims(result)
  } catch (error) {
    throw parseCallableError(error)
  }
}

export async function sendMagicLink(email: string, continueUrl: string): Promise<void> {
  try {
    await sendSignInLinkToEmail(getFirebaseAuth(), email, {
      url: continueUrl,
      handleCodeInApp: true,
    })
    window.localStorage.setItem(MAGIC_LINK_EMAIL_STORAGE_KEY, email)
  } catch (error) {
    throw parseCallableError(error)
  }
}

export function isMagicLink(url: string): boolean {
  return isSignInWithEmailLink(getFirebaseAuth(), url)
}

export async function completeMagicLink(url: string, email: string): Promise<AuthUser> {
  try {
    const credential = await signInWithEmailLink(getFirebaseAuth(), email, url)
    window.localStorage.removeItem(MAGIC_LINK_EMAIL_STORAGE_KEY)
    return toAuthUser(credential.user)
  } catch (error) {
    throw parseCallableError(error)
  }
}

export async function signInWithGoogle(): Promise<AuthUser> {
  try {
    const credential = await signInWithPopup(getFirebaseAuth(), new GoogleAuthProvider())
    return toAuthUser(credential.user)
  } catch (error) {
    throw parseCallableError(error)
  }
}

export async function signOut(): Promise<void> {
  try {
    await firebaseSignOut(getFirebaseAuth())
  } catch (error) {
    throw parseCallableError(error)
  }
}

export const refreshAuthClaims = callable<Record<string, never>, void>('refreshAuthClaims')

export interface CreateWorkspaceInput {
  workspaceName: string
  brandName: string
  contentLanguage: SupportedLocale
}

export const createWorkspace = callable<CreateWorkspaceInput, void>('createWorkspace')

export interface UpdateUserProfileInput {
  fullName: string
}

export const updateUserProfile = callable<UpdateUserProfileInput, void>('updateUserProfile')
